/**
 * Named recipe versions — the snapshot half of version history (PLAN-LAUNCH
 * D3).
 *
 * Structurally the `waterProfile.*` / `inventory.*` four: create (from a
 * fully-built document), rename, delete, restore. Nothing new to learn, and
 * nothing new to sync — a named version is an ordinary document written by
 * ordinary commands, so it replicates, undoes and survives offline exactly like
 * a batch does.
 *
 * `recipeSnapshot.create` takes its document whole for the same reason
 * `batch.create` does: building one needs a clock, an id and a locale, none of
 * which exist in this package. `buildRecipeSnapshotDoc` is the caller's helper.
 *
 * Deleting a recipe deliberately leaves its snapshots alone, exactly as it
 * leaves its batches alone (`document/batch.ts`): a snapshot is a complete
 * recipe document, so it stays restorable, and taking history away with the
 * recipe would make `recipe.delete` inexactly invertible.
 */

import {
  RECIPE_SNAPSHOTS_PER_RECIPE,
  type RecipeSnapshotDoc,
  snapshotsOfRecipe,
} from '../../document/recipe-snapshot';
import type { WorkspaceState } from '../../document/workspace';
import { RECIPE_SNAPSHOT_SCHEMA_VERSION } from '../../version';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { quoted, transition } from '../format';
import * as p from '../payloads';

/** A copy of `state` with one snapshot replaced (or added). */
function putSnapshot(state: WorkspaceState, snapshot: RecipeSnapshotDoc): WorkspaceState {
  return {
    ...state,
    recipeSnapshots: { ...state.recipeSnapshots, [snapshot.id]: snapshot },
  };
}

/** A copy of `state` with one snapshot removed. */
function dropSnapshot(state: WorkspaceState, snapshotId: string): WorkspaceState {
  const recipeSnapshots = { ...state.recipeSnapshots };
  delete recipeSnapshots[snapshotId];
  return { ...state, recipeSnapshots };
}

/** Look a snapshot's name up for a summary, tolerating its absence. */
function snapshotName(state: WorkspaceState, snapshotId: string): string {
  return state.recipeSnapshots[snapshotId]?.name ?? snapshotId;
}

/**
 * Save a named version of a recipe.
 *
 * Refuses a duplicate id, and refuses to grow one recipe's list past
 * {@link RECIPE_SNAPSHOTS_PER_RECIPE} — a bound rather than a policy: every
 * snapshot is a whole recipe document inside the workspace that is pushed over
 * the sync socket and mirrored into IndexedDB.
 */
export const recipeSnapshotCreate = defineCommand({
  type: 'recipeSnapshot.create',
  payload: p.recipeSnapshotCreatePayloadSchema,
  apply: (state, command) => {
    const { snapshotId, from, name } = command.payload;
    if (state.recipeSnapshots[snapshotId] !== undefined) {
      return {
        ok: false,
        error: commandError('conflict', `A saved version with id "${snapshotId}" already exists.`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    if (
      snapshotsOfRecipe(state.recipeSnapshots, from.recipeId).length >= RECIPE_SNAPSHOTS_PER_RECIPE
    ) {
      return {
        ok: false,
        error: commandError(
          'invalid-state',
          `This recipe already has ${RECIPE_SNAPSHOTS_PER_RECIPE} saved versions. Delete one to save another.`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    const doc: RecipeSnapshotDoc = {
      ...from,
      schemaVersion: RECIPE_SNAPSHOT_SCHEMA_VERSION,
      id: snapshotId,
      name: name ?? from.name,
      createdAt: command.timestamp,
      updatedAt: command.timestamp,
    };
    return { ok: true, state: putSnapshot(state, doc) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'recipeSnapshot.delete',
      { snapshotId: command.payload.snapshotId },
      command.timestamp,
    ),
  summarize: (_state, command) =>
    `Save recipe version ${quoted(command.payload.name ?? command.payload.from.name)}`,
});

/** Insert a saved version verbatim, timestamps included — an **upsert**. */
export const recipeSnapshotRestore = defineCommand({
  type: 'recipeSnapshot.restore',
  payload: p.recipeSnapshotRestorePayloadSchema,
  apply: (state, command) => ({
    ok: true,
    state: putSnapshot(state, command.payload.snapshot),
  }),
  invert: (state, command) => {
    const prior = state.recipeSnapshots[command.payload.snapshot.id];
    return prior === undefined
      ? inverseCommand(
          command,
          'recipeSnapshot.delete',
          { snapshotId: command.payload.snapshot.id },
          command.timestamp,
        )
      : inverseCommand(command, 'recipeSnapshot.restore', { snapshot: prior }, prior.updatedAt);
  },
  summarize: (_state, command) => `Restore recipe version ${quoted(command.payload.snapshot.name)}`,
});

/** Delete a saved version. Invertible: the inverse carries the whole document. */
export const recipeSnapshotDelete = defineCommand({
  type: 'recipeSnapshot.delete',
  payload: p.recipeSnapshotDeletePayloadSchema,
  apply: (state, command) => {
    const snapshot = state.recipeSnapshots[command.payload.snapshotId];
    if (snapshot === undefined) {
      return {
        ok: false,
        error: commandError(
          'not-found',
          `No saved version with id "${command.payload.snapshotId}".`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    return { ok: true, state: dropSnapshot(state, command.payload.snapshotId) };
  },
  invert: (state, command) => {
    const prior = state.recipeSnapshots[command.payload.snapshotId];
    return prior === undefined
      ? null
      : inverseCommand(command, 'recipeSnapshot.restore', { snapshot: prior }, prior.updatedAt);
  },
  summarize: (state, command) =>
    `Delete recipe version ${quoted(snapshotName(state, command.payload.snapshotId))}`,
});

/** Rename a saved version. */
export const recipeSnapshotRename = defineCommand({
  type: 'recipeSnapshot.rename',
  payload: p.recipeSnapshotRenamePayloadSchema,
  apply: (state, command) => {
    const snapshot = state.recipeSnapshots[command.payload.snapshotId];
    if (snapshot === undefined) {
      return {
        ok: false,
        error: commandError(
          'not-found',
          `No saved version with id "${command.payload.snapshotId}".`,
          { commandId: command.commandId, commandType: command.type },
        ),
      };
    }
    return {
      ok: true,
      state: putSnapshot(state, {
        ...snapshot,
        name: command.payload.name,
        updatedAt: command.timestamp,
      }),
    };
  },
  invert: (state, command) => {
    const prior = state.recipeSnapshots[command.payload.snapshotId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipeSnapshot.rename',
          { snapshotId: prior.id, name: prior.name },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Rename recipe version ${transition(
      quoted(snapshotName(state, command.payload.snapshotId)),
      quoted(command.payload.name),
    )}`,
});
